/**
 * 목록 로컬 캐시 공용 유틸리티 (HANDOVER §3-E-2)
 *
 * 화면 진입 시 직전 사본을 즉시 표시하여 체감 로딩 지연을 없애는 용도.
 * 서버 응답 도착 시 항상 최신 데이터로 교체되므로 TTL(유효기간) 없이 무기한 보관한다.
 *
 * [키 규칙]
 * - 접두사: moowoo_lc_
 * - 포맷: moowoo_lc_${userId}_${listKey}
 * - 사용자 ID 필수: 계정 전환 시 앞사람 데이터 오염 방지
 */

const CACHE_PREFIX = 'moowoo_lc_';

/**
 * 캐시 키 생성 헬퍼
 * @param {string} userId - 사용자 ID
 * @param {string} listKey - 목록 종류 식별자
 * @returns {string|null} 캐시 키 또는 유효하지 않을 경우 null
 */
const buildCacheKey = (userId, listKey) => {
  if (!userId || typeof userId !== 'string' || !userId.trim()) {
    return null;
  }
  if (!listKey || typeof listKey !== 'string' || !listKey.trim()) {
    return null;
  }
  return `${CACHE_PREFIX}${userId.trim()}_${listKey.trim()}`;
};

/**
 * 1. 저장: 목록 데이터를 로컬 캐시에 보관
 * @param {string} userId - 사용자 ID (없으면 저장하지 않고 조용히 반환)
 * @param {string} listKey - 목록 종류 식별자 (예: 'chat_list', 'group_chat_list' 등)
 * @param {any} data - 저장할 목록 데이터 (undefined인 경우 저장하지 않음)
 */
export function setListCache(userId, listKey, data) {
  try {
    if (typeof window === 'undefined' || !window.localStorage) return;
    if (data === undefined) return;

    const key = buildCacheKey(userId, listKey);
    if (!key) return;

    localStorage.setItem(key, JSON.stringify(data));
  } catch (_) {
    // 시크릿 모드, 용량 초과 등 스토리지 쓰기 실패 시 조용히 무시
  }
}

/**
 * 2. 조회: 키로 보관분을 꺼냄. 없거나 손상되면 null 반환
 * @param {string} userId - 사용자 ID
 * @param {string} listKey - 목록 종류 식별자
 * @returns {any|null} 보관된 데이터 또는 null
 */
export function getListCache(userId, listKey) {
  try {
    if (typeof window === 'undefined' || !window.localStorage) return null;

    const key = buildCacheKey(userId, listKey);
    if (!key) return null;

    const raw = localStorage.getItem(key);
    if (!raw) return null;

    return JSON.parse(raw);
  } catch (_) {
    // 파싱 오류, 스토리지 접근 불가 등 발생 시 null 반환
    return null;
  }
}

/**
 * 3. 폐기: 이 유틸이 생성한 접두사(moowoo_lc_)를 가진 캐시 키 전체 삭제
 * 로그아웃 및 계정 전환 시 호출된다.
 * 기존 키(myTone, myLanguage, HAS_SEEN_INTRO, LAST_AUTH_EMAIL 등)는 절대 건드리지 않는다.
 */
export function clearListCache() {
  try {
    if (typeof window === 'undefined' || !window.localStorage) return;

    const keysToRemove = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && key.startsWith(CACHE_PREFIX)) {
        keysToRemove.push(key);
      }
    }

    keysToRemove.forEach((key) => {
      try {
        localStorage.removeItem(key);
      } catch (_) {}
    });
  } catch (_) {
    // 스토리지 접근 오류 발생 시 로그아웃 흐름을 방해하지 않도록 무시
  }
}

/**
 * 4. 개별 폐기: 특정 목록 종류 식별자(listKey)의 사본을 사용자 ID와 무관하게 전부 삭제
 * 목록 내용이 변경되는 지점(방 신설, 방 나가기, 프로필/별칭 변경 등)에서 호출된다.
 * 예) 'chat_list' 를 넘기면 moowoo_lc_ 로 시작하고 _chat_list 로 끝나는 키만 선별 삭제한다.
 * moowoo_lc_ 이외의 다른 로컬스토리지 키는 절대 건드리지 않는다.
 * @param {string} listKey - 폐기할 목록 종류 식별자 (예: 'chat_list')
 */
export function invalidateListCache(listKey) {
  try {
    if (typeof window === 'undefined' || !window.localStorage) return;
    if (!listKey || typeof listKey !== 'string' || !listKey.trim()) return;

    const suffix = `_${listKey.trim()}`;
    const keysToRemove = [];

    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      // moowoo_lc_ 접두사와 _${listKey} 접미사를 모두 만족하는 키만 선별
      if (key && key.startsWith(CACHE_PREFIX) && key.endsWith(suffix)) {
        keysToRemove.push(key);
      }
    }

    keysToRemove.forEach((key) => {
      try {
        localStorage.removeItem(key);
      } catch (_) {}
    });
  } catch (_) {
    // 스토리지 접근 오류 발생 시 호출부의 비즈니스 로직을 방해하지 않도록 예외 흡수
  }
}
